import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { AIInsightsData, PDFColors } from "../types";

/**
 * Add AI Insights section to the PDF (Weather, Air Quality, Carbon)
 */
export const addAIInsightsSection = (
  doc: jsPDF,
  aiData: AIInsightsData,
  startY: number,
  colors: PDFColors,
): void => {
  if (!aiData.weather && !aiData.airQuality && !aiData.carbon) return;

  const pageWidth = doc.internal.pageSize.getWidth();
  let currentY = startY;

  // Rule 8: Block 1 - Real-time environmental conditions
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.text("REAL-TIME ENVIRONMENTAL CONDITIONS", 14, currentY);
  currentY += 10;

  const weatherAirQualityBody = [];
  if (aiData.weather) {
    weatherAirQualityBody.push([
      "Atmospheric Conditions",
      `${aiData.weather.temperature}°C, ${aiData.weather.description}`,
      `Humidity: ${aiData.weather.humidity}% | Wind: ${aiData.weather.windSpeed} km/h`,
    ]);
  }
  if (aiData.airQuality) {
    weatherAirQualityBody.push([
      "Air Quality Index (AQI)",
      `${aiData.airQuality.aqi} (${aiData.airQuality.level})`,
      aiData.airQuality.description,
    ]);
  }

  if (weatherAirQualityBody.length > 0) {
    autoTable(doc, {
      startY: currentY,
      head: [["Metric Category", "Observation", "Technical Details"]],
      body: weatherAirQualityBody,
      theme: "striped",
      headStyles: {
        fillColor: colors.primary,
        textColor: [255, 255, 255] as [number, number, number],
      },
      margin: { left: 14, right: 14 },
    });
    currentY = (doc as any).lastAutoTable.finalY + 15;
  }

  // Rule 8: Block 2 - Carbon, ESG, and projections
  if (aiData.carbon) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.text("CARBON ANALYTICS & ESG PROJECTIONS", 14, currentY);
    currentY += 10;

    const carbonBody = [
      [
        "Metric Carbon Output",
        `${aiData.carbon.totalEmissions.toFixed(2)} million tonnes CO2`,
        "Total annual CO2 equivalent emissions recorded for this region.",
      ],
      [
        "Regional Trend Analysis",
        `${aiData.carbon.trend.toUpperCase()} (${aiData.carbon.growthRate > 0 ? "+" : ""}${aiData.carbon.growthRate.toFixed(2)}%)`,
        "Observed variation in emission patterns over the last reporting cycle.",
      ],
      [
        "Institutional ESG Rating",
        aiData.carbon.esgRating,
        "Environmental, Social, and Governance score based on sustainability metrics.",
      ],
      [
        "Reduction Roadmap",
        `${aiData.carbon.projectedReduction}% Projected`,
        "Estimated reduction in carbon intensity by the assigned target year.",
      ],
    ];

    autoTable(doc, {
      startY: currentY,
      head: [["Strategic Metric", "Regional Value", "Governance Context"]],
      body: carbonBody,
      theme: "grid",
      headStyles: {
        fillColor: [46, 125, 50] as [number, number, number],
        textColor: [255, 255, 255] as [number, number, number],
      },
      margin: { left: 14, right: 14 },
    });
  }
};
